import { getClientBuildId } from './buildVersion';

/**
 * Re-load `_buildManifest.js` when the page's own copy never arrived.
 *
 * Next.js puts `/_next/static/<buildId>/_buildManifest.js` in the HTML as a deferred script and never
 * retries it. If that one request fails or stalls (flaky clinic Wi-Fi, a doctor-shell worker cache miss
 * while offline), `self.__BUILD_MANIFEST` stays empty for the life of the tab: every `router.prefetch`
 * rejects with «Failed to load client build manifest» after 3.8 s and every client navigation degrades to
 * a full page load. RUM 2026-11-05: ~500 such errors a day, one device at a time, 93% on /apphome, while
 * the file itself answered 200 from the CDN.
 *
 * The manifest script ends with `self.__BUILD_MANIFEST_CB && self.__BUILD_MANIFEST_CB()`, which is what
 * Next's route loader waits on, so loading it again heals the running tab without a reload.
 */

const RETRY_DELAYS_MS = [0, 2000, 6000];

let started = false;

const hasManifest = () => Boolean((window as any).__BUILD_MANIFEST);

const manifestUrl = (buildId: string) => {
  const assetPrefix = String((window as any).__NEXT_DATA__?.assetPrefix ?? '').replace(/\/$/, '');
  return `${assetPrefix}/_next/static/${encodeURIComponent(buildId)}/_buildManifest.js`;
};

const loadManifest = (attempt: number) => {
  if (hasManifest() || attempt >= RETRY_DELAYS_MS.length) return;
  window.setTimeout(() => {
    if (hasManifest()) return;
    const buildId = getClientBuildId();
    if (!buildId) return;
    const script = document.createElement('script');
    script.src = manifestUrl(buildId);
    script.async = true;
    script.onload = () => {
      if (!hasManifest()) loadManifest(attempt + 1);
    };
    script.onerror = () => {
      script.remove();
      loadManifest(attempt + 1);
    };
    document.head.appendChild(script);
  }, RETRY_DELAYS_MS[attempt]);
};

const check = () => {
  if (!hasManifest()) loadManifest(0);
};

/** Call once on the client; checks after `load`, when every deferred script has run or failed. */
export const ensureBuildManifest = () => {
  if (typeof window === 'undefined' || started) return;
  started = true;
  if (document.readyState === 'complete') check();
  else window.addEventListener('load', check, { once: true });
};

/** Test hook. */
export const resetBuildManifestRecoveryForTests = () => {
  started = false;
};
